import React from "react";

/**
 * Top-level ErrorBoundary.
 *
 * Two jobs:
 *  1. Stale-deploy recovery. After a redeploy, a user holding a cached old
 *     index.html requests hashed JS chunks that no longer exist -> the browser
 *     throws a ChunkLoadError ("Loading chunk N failed"). We detect that and do
 *     ONE automatic hard reload (guarded by sessionStorage so we can never loop)
 *     which pulls the fresh index.html + new chunk names.
 *  2. Any other render/runtime error shows a friendly fallback with a manual
 *     "Reload" button instead of a silent blank white screen.
 */
const RELOAD_FLAG = "__lusuku_chunk_reloaded__";

const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = error.name || "";
  const msg = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /import\(\).*failed/i.test(msg)
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      // Reload exactly once to fetch the fresh build; the flag prevents loops
      // if the reload itself somehow re-triggers the error.
      let alreadyReloaded = false;
      try {
        alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === "1";
        if (!alreadyReloaded) {
          window.sessionStorage.setItem(RELOAD_FLAG, "1");
        }
      } catch (_) {
        // sessionStorage blocked (private mode / storage disabled) — fall
        // through to the manual fallback UI rather than risk a reload loop.
      }
      if (!alreadyReloaded) {
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    try {
      window.sessionStorage.removeItem(RELOAD_FLAG);
    } catch (_) {
      /* ignore */
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "2rem",
            fontFamily: "Urbanist, Arial, sans-serif",
          }}
        >
          <h2 style={{ marginBottom: "0.5rem", color: "#222" }}>
            Something went wrong loading the page
          </h2>
          <p style={{ color: "#666", marginBottom: "1.5rem" }}>
            Please reload to get the latest version.
          </p>
          <button
            onClick={this.handleReload}
            style={{
              background: "#00d50e",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
